<template>
  <v-flex
    pa-0
    ma-0>
    <v-layout
      row
      wrap>
      <v-flex xs12>
        <v-btn
          class="right"
          color="success"
          @click="add()">
          {{ $t('Add New') }}
        </v-btn>
      </v-flex>
      <v-flex
        v-if="list.length"
        xs12
      >
        <v-list
          two-line
          subheader
        >
          <v-list-tile
            v-for="lang in list"
            :key="lang.code"
            avatar
          >
            <v-list-tile-avatar
              class="pointer"
              @click.prevent="editLang(lang)">
              {{ lang.flag }}
            </v-list-tile-avatar>

            <v-list-tile-content
              class="pointer"
              @click.prevent="editLang(lang)"
            >
              <v-list-tile-title class="tile-height">
                <v-chip small>
                  <v-avatar
                    color="secondary"
                    class="white--text"
                  >
                    {{ lang.code }}
                  </v-avatar>
                  {{ lang.name }}
                </v-chip>
              </v-list-tile-title>
              <v-list-tile-sub-title>
                {{ $t('Locale') }}: {{ lang.iso }} / {{ $t('Direction') }}: {{ lang.dir }}
              </v-list-tile-sub-title>
            </v-list-tile-content>

            <v-list-tile-action>
              <v-btn
                :disabled="lang.default"
                icon
                ripple
                @click.prevent="remove(lang.code)"
              >
                <v-icon color="error lighten-1">delete</v-icon>
              </v-btn>
            </v-list-tile-action>
          </v-list-tile>
        </v-list>
      </v-flex>
    </v-layout>
    <v-dialog
      v-model="adding"
      width="500"
    >
      <v-card
        row
        wrap
      >
        <v-card-title>
          <span class="title">{{ $t('Language') }}</span>
        </v-card-title>

        <v-divider/>

        <v-card-text>
          <language-form v-model="lang"/>
        </v-card-text>

        <v-divider/>

        <v-card-actions>
          <v-spacer/>
          <v-btn
            flat
            small
            @click="cancel()"
          >
            {{ $t('Cancel') }}
          </v-btn>
          <v-btn
            :disabled="!canSave"
            flat
            small
            color="success"
            @click="save()"
          >
            {{ $t('Save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LanguageForm from '@/modules/Settings/components/Language/LanguageForm'

export default {
  name: 'LanguagesList',
  components: { LanguageForm },

  data () {
    return {
      list: [],
      adding: false,
      lang: {
        name: '',
        code: '',
        flag: '',
        iso: '',
        dir: ''
      }
    }
  },

  computed: {
    ...mapGetters(['defaultLang']),

    canSave () {
      return Object.values(this.lang).every(val => val !== '')
    }
  },

  watch: {
    defaultLang: function () {
      this.mapDefault()
    }
  },

  async mounted () {
    this.list = await this.$http.make('language').get()
    this.mapDefault()
  },

  methods: {
    ...mapActions(['setLanguagesList', 'setDefaultLang']),

    add () {
      this.adding = true
    },

    cancel () {
      this.adding = false
      this.lang = {
        name: '',
        code: '',
        flag: '',
        iso: '',
        dir: ''
      }
    },

    editLang (lang) {
      this.lang = {
        name: lang.name,
        code: lang.code,
        flag: lang.flag,
        iso: lang.iso,
        dir: lang.dir
      }
      this.adding = true
    },

    save () {
      const lang = this.$http.make('language', this.lang)
      const exists = this.list.some(l => l.code === lang.code)

      lang.save().then(() => {
        if (!exists) {
          this.list.push(this.lang)
        } else {
          this.list = this.list.map(l => {
            if (l.code === lang.code) {
              l = lang
            }
            return l
          })
        }

        this.setLanguagesList(this.list)
        this.setDefaultLang(this.defaultLang.code)
        this.$toast.success(this.$t('Language created'))
      }).catch(e => {
        this.$toast.error(e)
      }).finally(() => {
        this.cancel()
      })
    },

    async remove (code) {
      if (this.list.length === 1) {
        this.$toast.warning(this.$t('At least one language needs to be defined'))
        return
      }

      if (this.defaultLang.code === code) {
        this.$toast.warning(this.$t('Cannot remove default language'))
        return
      }

      const answer = await this.$root.$confirm(this.$t('Attention'), this.$t('Are you sure want to delete language?'), { color: 'primary' })
      if (!answer) {
        return
      }

      const lang = this.$http.make('language')
      lang.delete(code).then(() => {
        this.list = this.list.filter(item => item.code !== code)
        this.setLanguagesList(this.list)
        this.$toast.success(this.$t('Language deleted'))
      }).catch(e => {
        this.$toast.error(e)
      }).finally(() => {
        this.cancel()
      })
    },

    mapDefault () {
      if (!this.defaultLang) {
        return
      }

      this.list = this.list.map(lang => {
        lang.default = lang.code === this.defaultLang.code
        return lang
      })
    }
  }
}
</script>

<style scoped>
  .tile-height {
    height: 30px;
  }
</style>
